/**
 * Terms of Service — site usage terms.
 */
import Seo from "../components/Seo";
import { Link } from "react-router-dom";

export default function TermsOfService() {
  return (
    <main className="min-h-screen bg-[var(--ssc-paper)]">
      <Seo title="Terms of Service · Scott Sells Chevys" description="Terms governing use of the Scott Sells Chevys website and CRM portal." />
      <header className="bg-[var(--ssc-navy)] text-white px-4 sm:px-8 py-10">
        <div className="text-[10px] font-extrabold uppercase tracking-[0.25em] text-[var(--ssc-blue)]">Legal</div>
        <h1 className="text-3xl sm:text-4xl font-black mt-2">Terms of Service</h1>
        <p className="text-white/70 mt-1 text-sm">Last updated: February 29, 2026</p>
      </header>
      <article className="px-4 sm:px-8 py-10 max-w-3xl mx-auto text-[var(--ssc-ink)] space-y-5 leading-relaxed" data-testid="tos-page">
        <Section title="Acceptance">
          <p>By using ScottSellsChevys.com or any related apps, you agree to these terms and our <Link to="/privacy" className="text-[var(--ssc-blue)] underline">Privacy Policy</Link>. If you do not agree, please don't use the site.</p>
        </Section>
        <Section title="What We Provide">
          <p>This site is a personal marketing and lead-management website for Scott Hall's Chevrolet sales activity. We offer:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Vehicle inventory browsing and saved searches</li>
            <li>A self-service trade-in appraisal tool</li>
            <li>"Build My Own Deal" payment calculator</li>
            <li>Direct messaging with Scott</li>
            <li>A customer portal to track your deal status</li>
          </ul>
        </Section>
        <Section title="Estimates Are Not Offers">
          <p>The trade appraisal, payment calculator, and any AI-generated content on this site are estimates only — they are not binding offers, financing approvals, or commitments to buy/sell. Final terms always require an in-person inspection of the trade vehicle, a credit application, and a signed contract.</p>
        </Section>
        <Section title="Accuracy of Your Information">
          <p>You agree that everything you submit (contact info, trade details, financing info) is accurate to the best of your knowledge. Submitting false information may result in cancellation of any pending quote.</p>
        </Section>
        <Section title="Pricing & Inventory">
          <p>We try to keep inventory, prices, and incentives up-to-date, but errors happen. If a vehicle is listed at an obviously incorrect price (a typo, system error, or omitted fee), we reserve the right to correct it before completing the sale. Government taxes, title, license, and dealer documentation fees are extra unless explicitly stated otherwise.</p>
        </Section>
        <Section title="Communication">
          <p>By submitting your contact information, you agree we may follow up by phone, text, and/or email. Standard message and data rates may apply. Reply STOP to opt out of texts; click "unsubscribe" in any email to opt out of email follow-ups.</p>
        </Section>
        <Section title="Intellectual Property">
          <p>All site design, branding, photographs of Scott, customer photographs (with permission), trade tools, AI prompts, and software code are the property of Scott Hall or his licensors. You may not copy, reproduce, resell, or scrape this site without written permission. Inventory feeds may be subject to the source dealership's terms.</p>
        </Section>
        <Section title="Customer Portal Use">
          <p>Your deal code is private. Anyone who has it can see the status of your deal. Don't share it. We're not responsible for unauthorized access caused by you sharing the code.</p>
        </Section>
        <Section title="Liability Limits">
          <p>This site is provided "as is." We don't guarantee it will be available without interruption, free of bugs, or error-free. To the maximum extent permitted by law, Scott Hall and Scott Sells Chevys are not liable for any indirect, incidental, special, or consequential damages arising from your use of the site (lost data, missed sales opportunities, etc.). Our total aggregate liability is limited to the amount you've paid us in the past 12 months (which, for most site users, is $0).</p>
        </Section>
        <Section title="Indemnification">
          <p>You agree to defend, indemnify, and hold harmless Scott Hall and Scott Sells Chevys from any claims arising out of your misuse of the site, your violation of these terms, or your violation of any rights of a third party.</p>
        </Section>
        <Section title="Governing Law">
          <p>These terms are governed by the laws of the State of Ohio. Any disputes will be resolved in the courts of Franklin County, Ohio.</p>
        </Section>
        <Section title="Changes">
          <p>We may update these terms from time to time. The "Last updated" date will reflect material changes. Continued use of the site after we post changes constitutes acceptance.</p>
        </Section>
        <Section title="Contact">
          <p><a className="text-[var(--ssc-blue)] underline" href="mailto:askscott@scottsellschevys.com">askscott@scottsellschevys.com</a> · 614-535-8673 · Central Ohio.</p>
        </Section>
      </article>
    </main>
  );
}

const Section = ({ title, children }) => (
  <section>
    <h2 className="text-xl font-black text-[var(--ssc-navy)] mb-2">{title}</h2>
    <div className="space-y-2 text-sm">{children}</div>
  </section>
);
